import {
    immersiveReaderErrorTitle,
    immersiveReaderErrorText,
} from './showInImmersiveReaderOrchestrator.locstring.json';
import loc from 'owa-localize';
import { mutatorAction, orchestrator } from 'satcheljs';
import { default as showInImmersiveReader } from '../actions/showInImmersiveReader';
import { default as delay } from '../utils/delay';
import { getStore, ImmersiveReaderFrameState } from 'owa-immersive-reader-store';
import immersiveReaderProcessText from 'owa-immersive-reader-store/lib/services/immersiveReaderProcessText';
import { PerformanceDatapoint, DatapointStatus } from 'owa-analytics';
import { confirm } from 'owa-confirm-dialog';
import { default as populateItemId } from 'owa-service/lib/factory/itemId';
import { ClientItemId } from 'owa-client-ids';

// This interface is defined by Learning Tools
interface LinguisticModelMessage {
    messageType: 'LinguisticModel';
    messageValue: {
        data?: any;
        meta: any;
        status: number;
        error?: string;
    };
}

const VERSION2_0: string = '2.0';

export default orchestrator(showInImmersiveReader, async actionMessage => {
    const itemId: ClientItemId = actionMessage.itemId;
    const store = getStore();

    // Don't proceed if itemId is null, or we are already in the middle of a service call, or
    // the immersive reader is already showing.
    if (
        !itemId ||
        store.isLoading ||
        store.immersiveReaderFrameState !== ImmersiveReaderFrameState.Closed
    ) {
        return;
    }

    const dp = new PerformanceDatapoint('ShowInImmersiveReader');
    dp.addCustomData({ sessionId: store.sessionId, subAction: actionMessage.subActionSource });
    dp.addActionSource(actionMessage.actionSource);

    toggleLoadingState(true);

    // Show iframe and begin loading immersive reader app
    toggleShowingState(true);

    // Track load duration
    const startTime = new Date().getUTCMilliseconds();

    let success = true;
    let readerModel: any = null;
    let error: string = null;
    try {
        // Make service call
        const response = await immersiveReaderProcessText(
            populateItemId({ Id: itemId.Id }),
            store.sessionId,
            VERSION2_0,
            itemId.mailboxInfo && itemId.mailboxInfo.requestOptions
        );
        dp.addCheckmark('ImmersiveReaderProcessText_Done');

        // Parse ReaderModel from service call response
        if (!response || !response.ReaderModelJson) {
            success = false;
            error = 'Response from service call was null or did not contain a ReaderModelJson';
        } else {
            readerModel = JSON.parse(response.ReaderModelJson);
        }

        // Store API response in store, which will get picked up by ImmersiveReaderContainer
        if (success) {
            const endTime = new Date().getUTCMilliseconds();
            const launchToPostMessageSentDurationInMs: string = (endTime - startTime).toString();

            // Generate a specific message that gets sent to the Immersive Reader via window.postMessage
            let apiResponse: any = null;
            readerModel.launchToPostMessageSentDurationInMs = launchToPostMessageSentDurationInMs;
            apiResponse = <LinguisticModelMessage>{
                messageType: 'LinguisticModel',
                messageValue: readerModel,
            };
            setApiResponse(apiResponse);

            // Wait to ensure that immersive reader frame is ready. If, for some reason, the immersive
            // reader iframe never loads (due to a failure in the immersive reader service), then OWA
            // should show an error dialog after waiting some amount of time (in this case, 60 seconds).
            const maxRetries = 120;
            let retryCount = 0;
            while (
                retryCount < maxRetries &&
                getStore().immersiveReaderFrameState === ImmersiveReaderFrameState.Showing
            ) {
                await delay(500);
                retryCount++;
            }
            if (getStore().immersiveReaderFrameState === ImmersiveReaderFrameState.Showing) {
                success = false;
                error = 'Frame never got to Ready state';
            }
        }
    } catch (ex) {
        success = false;
        dp.addCheckmark('CaughtException');
        error = ex.toString();
    } finally {
        toggleLoadingState(false);
    }

    if (success) {
        dp.end();
    } else {
        // Hide immersive reader if something went wrong
        toggleShowingState(false);

        dp.endWithError(DatapointStatus.ServerError, new Error(error));

        // Show error dialog
        confirm(loc(immersiveReaderErrorTitle), loc(immersiveReaderErrorText), false, {
            hideCancelButton: true,
        });
    }
});

const toggleLoadingState = mutatorAction(
    'toggleImmersiveReaderLoadingState',
    (isLoading: boolean) => {
        getStore().isLoading = isLoading;
    }
);

const toggleShowingState = mutatorAction(
    'toggleImmersiveReaderShowingState',
    (isShowing: boolean) => {
        getStore().immersiveReaderFrameState = isShowing
            ? ImmersiveReaderFrameState.Showing
            : ImmersiveReaderFrameState.Closed;
    }
);

const setApiResponse = mutatorAction('setImmersiveReaderApiResponse', (apiResponse: any) => {
    getStore().apiResponse = apiResponse;
});
